import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { VisitsDataService, VisitsColumnsService } from '../../services';
import { DialogVisitFormComponent } from './visit-form/visit-form.component';
import { DialogVisitDetailsComponent } from './visit-details/visit-details.component';
import { Visit, VisitsResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-visits',
  templateUrl: './visits.component.html',
  styleUrls: ['./visits.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class VisitsComponent implements OnInit, OnDestroy {

  private visitAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private visitEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private visitDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public visitToDelete: Visit = {};
  public visits: Visit[] = [];
  public visit: Visit = {};
  public selectedVisits: Visit[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private visitsDataService: VisitsDataService,
    private visitsColumnsService: VisitsColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('VisitsComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Visits - Management');
    this.primengConfig.ripple = true;
    this.getVisits();
  }

  ngOnDestroy(): void {
    console.log('VisitsComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getVisits(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.visitsDataService.getVisits(this.parameters).subscribe((response: VisitsResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.visits = response.results;
        } else {
          this.visits = this.visits.concat(response.results);
        }
        if (response.inlineCount > this.visits.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getVisits();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('VISITSNOTLOADED')});
    });
  }

  importVisits(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.visitsDataService.addVisits(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('VISITSCREATED')});
                if (proceed === j) {
                  this.getVisits();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('VISITSNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('VISITSNOTCREATED') + ' ' + msg});
            });
          } else {
            this.visitsDataService.updateVisits(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('VISITSUPDATED')});
                if (proceed === j) {
                  this.getVisits();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('VISITSNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('VISITSNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.visitsColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('visits.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedVisits() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Visits?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.visitsDataService.deleteVisits(this.selectedVisits).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Visits Deleted', life: 3000});
            this.getVisits();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Visits not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Visits not deleted'});
        });
        this.selectedVisits = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.visits, this.columns, 'Visits');
  }

  exportExcel() {
    ToolsService.exportExcel(this.visits, this.columns, 'Visits');
  }

  exportCsv() {
    ToolsService.exportCsv(this.visits, this.columns, 'Visits');
  }

  refresh() {
    this.visitsDataService.clearCache();
    this.getVisits();
  }

  visitAdd(): void {
    if (!this.navigation.getParamValue('visits.addInDialog', true)) {
      this.router.navigate(['/visits/add']);
      return;
    }
    this.visitAddDialog = this.dialogService.open(DialogVisitFormComponent, {
      header: '', // this.translate.instant('NEWVISIT'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        visit: {id: null},
        users: this.visitsColumnsService.users,
      },
    });

    this.visitAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'Visit added'});
        this.getVisits();
      }
    });
  }

  visitEdit(visit: Visit): void {
    if (!this.navigation.getParamValue('visits.editInDialog', true)) {
      this.router.navigate(['/visits/edit/' + visit.id]);
      return;
    }
    this.visitEditDialog = this.dialogService.open(DialogVisitFormComponent, {
      header: '', // this.translate.instant('EDITVISIT') + ' ' + visit.ip,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        visit: visit,
        users: this.visitsColumnsService.users,
      },
    });

    this.visitEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'Visit updated'});
        this.getVisits();
      }
    });
  }

  visitDetails(visit: Visit): void {
    if (!this.navigation.getParamValue('visits.detailsInDialog', true)) {
      this.router.navigate(['/visits/details/' + visit.id]);
      return;
    }
    this.visitDetailsDialog = this.dialogService.open(DialogVisitDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        visit: visit,
      }
    });
  }

  visitDelete(visit: Visit): void {
    this.visitToDelete = visit;
    this.messageService.clear();
    this.messageService.add({
      key: 'visitDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + visit.ip + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.visitsDataService.deleteVisit(this.visitToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Visit deleted'});
      this.getVisits();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Visit not deleted'});
    });
  }

  onDeleteReject(): void {
    this.visitToDelete = {};
    this.messageService.clear('visitDelete');
  }

}
